import { Flex, Box, Text } from "rebass";
import { default as NextBase } from "next/link";
import { useState, useEffect } from "react";
import { gql } from "@apollo/client";
import client from "../../lib/apollo-client.js";
// Component Imports
import {
	CardSlider,
	Layout,
	Loading,
	Error,
	WaveOne,
	WaveTwo,
	WaveOneMobile,
	WaveTwoMobile,
} from "../../components";
// Config
import getConfig from "next/config";
const { publicRuntimeConfig } = getConfig();
const { STRIPE_PUBLISHABLE_KEY } = publicRuntimeConfig;
import { layoutConfig } from "../../styleguide/theme";
import { donationOptions, infoSection } from "../../lib/donate/helpers.js";
import { colors } from "../../styleguide/theme";
import { LayoutPadding } from "../../components/utilities";

const DonationOptions = () => (
	<Flex
		width="100%"
		flexDirection={["column", "column", "column", "row"]}
		css={`
			position: relative;
			overflow: hidden;
		`}
	>
		{donationOptions.map((option, i) => (
			<DonationBox key={option.title} {...option} index={i} />
		))}
		<Flex
			width={["100%"]}
			css={`
				position: absolute;
				height: 100%;

				@media only screen and (max-width: 1055px) {
					display: none;
				}
			`}
			alignItems="flex-end"
		>
			<WaveOne />
		</Flex>
		<Flex
			width={["100%"]}
			css={`
				position: absolute;
				height: 100%;

				@media only screen and (max-width: 1055px) {
					display: none;
				}
			`}
			alignItems="flex-end"
		>
			<WaveTwo />
		</Flex>
		<Flex
			width={["100%"]}
			css={`
				position: absolute;
				height: 100%;

				@media only screen and (min-width: 1055px) {
					display: none;
				}
			`}
			justifyContent="flex-end"
		>
			<WaveOneMobile />
		</Flex>
		<Flex
			width={["100%"]}
			css={`
				position: absolute;
				height: 100%;

				@media only screen and (min-width: 1055px) {
					display: none;
				}
			`}
			justifyContent="flex-end"
		>
			<WaveTwoMobile />
		</Flex>
	</Flex>
);

const getDonationBoxBackground = (type) => {
	switch (type) {
		case "standard":
			return "#006A74;";
		case "monthly":
			return "#15426ac7;";
		case "tribute":
			return "linear-gradient(192deg, #018DF7 -7.03%, #018CF5 104.55%);";
	}
};

const DonationBox = ({ type, title, description, href, index, ...rest }) => {
	return (
		<Flex
			px={layoutConfig.paddingX}
			py={layoutConfig.paddingY}
			flexDirection="column"
			width={["100%", "100%", "100%", "33.4%"]}
			css={`
				background: ${getDonationBoxBackground(type)}
				border: 0.5px solid white;
			`}
			{...rest}
		>
			{/* Accent element */}
			<Box
				css={`
					background-color: white;
					width: 50px;
					height: 5px;
					font-size: 28px;
					margin-bottom: 8px;
				`}
			/>
			<Text
				as="h3"
				fontWeight={700}
				fontSize={["1.8rem", "1.8rem", "2rem"]}
				color="white"
				my={[3]}
				dangerouslySetInnerHTML={{ __html: title }}
			/>
			<Text as="p" color="white">
				{description}
			</Text>
			<Flex
				alignItems="flex-end"
				css={`
					flex-grow: 1;
					z-index: 1;
				`}
			>
				<NextBase passHref href={href}>
					<Flex
						bg={"white"}
						width="150px"
						justifyContent="center"
						alignItems="center"
						mt={[6]}
						tabIndex={0}
						css={`
							height: 50px;
							box-shadow: 0px 4px 150px rgba(102, 153, 12, 0.6);
							border-radius: 6px;
							cursor: pointer;
							:active {
								border: 1px solid black;
							}
						`}
					>
						<Text as="span" fontWeight={600}>
							Donate Now
						</Text>
					</Flex>
				</NextBase>
			</Flex>
		</Flex>
	);
};

const InfoSection = ({ data }) => (
	<Flex
		bg="#FFFFFF"
		flexDirection={["column", "column", "column", "row"]}
		mb={layoutConfig.paddingY}
	>
		{data.map((props) => (
			<InfoBox key={props.title} {...props} />
		))}
	</Flex>
);

const InfoBox = ({
	title,
	description,
	descriptionLineTwo,
	href,
	buttonTitle,
	...rest
}) => (
	<Flex
		bg={"#FFFFFF"}
		px={layoutConfig.paddingX}
		mt={layoutConfig.paddingY}
		flexDirection="column"
		width={["100%", "100%", "100%"]}
		{...rest}
	>
		<Flex flexDirection="column" width={["100%", "100%", "100%"]}>
			<Box
				css={`
					background-color: ${colors.lightblue};
					width: 50px;
					height: 5px;
					font-size: 28px;
					margin-bottom: 8px;
				`}
			/>
			<Text
				as="h3"
				fontWeight={700}
				color="#000000"
				fontSize={["1.8rem", "1.8rem", "2rem"]}
				my={[3]}
			>
				{title}
			</Text>
			<Text as="p" color="#000000">
				{description}
			</Text>
			{descriptionLineTwo && (
				<Text mt={2} as="p" color="#000000">
					{descriptionLineTwo}
				</Text>
			)}
			{href && buttonTitle && (
				<Flex
					alignItems="flex-end"
					css={`
						flex-grow: 1;
					`}
				>
					<NextBase passHref href={href}>
						<Flex
							tabIndex={0}
							bg="#FFFFFF"
							width="150px"
							justifyContent="center"
							alignItems="center"
							mt={[6]}
							css={`
								height: 50px;
								border-radius: 6px;
								border: 1px solid black;
								cursor: pointer;
							`}
						>
							<Text as="span" fontWeight={600}>
								{buttonTitle}
							</Text>
						</Flex>
					</NextBase>
				</Flex>
			)}
		</Flex>
	</Flex>
);

const GET_CAMPAIGN_INDEX = gql`
	query getCampaignIndexPage($first: Int!) {
		recentCampaigns: campaigns(first: $first) {
			edges {
				node {
					id
					jumbotronImage {
						rendition(format: "jpeg", min: "1840x1083") {
							url
						}
					}
					jumbotronTitle
				}
			}
		}

		donationIndex {
			edges {
				node {
					jumbotronTitle
				}
			}
		}
	}
`;

const gqlVariables = {
	first: 10,
};

const Index = ({ data, error }) => {
	if (error) {
		return <Error />;
	}

	const recentCampaigns = data.recentCampaigns.edges.map((c) => {
		return {
			id: c.node.id,
			title: c.node.jumbotronTitle,
			image: c.node.jumbotronImage.rendition.url,
			href: {
				pathname: `/campaigns/campaign/${c.node.jumbotronTitle
					.split(" ")
					.join("-")}`,
				query: { id: c.node.id },
			},
		};
	});

	const { jumbotronTitle } = data.donationIndex.edges[0].node;

	return (
		<React.Fragment>
			<Layout>
				<Box
					css={`
						color: white;
						height: 150px;
					`}
				/>
				<Box
					width={["100%", "100%", "100%", "50%"]}
					pt={8}
					pb={8}
					px={layoutConfig.paddingX}
					css={`
						z-index: 5;
					`}
				>
					<Text as="h1" fontWeight={500} color="#333">
						{jumbotronTitle}
					</Text>
				</Box>
				<DonationOptions />
				<InfoSection data={infoSection} />
				<Flex width={"100%"} bg={"darkblue"} color="#FFF">
					<LayoutPadding py={10}>
						{/* CONTENT CONTAINER */}
						<Flex flexDirection={"column"}>
							<Flex justifyContent="space-between">
								<Flex
									width={["100%", "100%", "100%", "100%"]}
									mx={[0, 0, 4]}
									flexDirection={"column"}
									alignItems="center"
									textAlign="center"
								>
									{/* TITLE */}
									<Text
										as="h2"
										fontSize={[6, 6, 8, 8]}
										pb={6}
										css={`
											line-height: ${1};
											text-transform: uppercase;
											letter-spacing: 1.8px;
											@media only screen and (max-width: 768px) {
												letter-spacing: 0px;
											}
										`}
									>
										Mezuzah Campaign
									</Text>

									<Text
										as="h3"
										fontWeight={100}
										fontSize={[4]}
										pb={5}
										css={``}
									>
										A meaningful way to honour your family,
										memorialize a loved one, or celebrate a
										special occasion.
									</Text>
									<Text
										as="p"
										fontWeight={100}
										pb={8}
										mx={10}
										css={`
											line-height: 20px;
										`}
									>
										Please consider dedicating a Mezuzah at
										either the Lou Fruitman Reena Residence
										or the Frankfort Family Reena Residence.
										Donations of $1,800 will be inscribed on
										a beautiful plaque displayed in the
										residence lobby.
									</Text>
									<NextBase
										passHref
										href={"/donate/mezuzah-campaign"}
									>
										<Flex
											bg={"white"}
											color="#333"
											width="150px"
											justifyContent="center"
											alignItems="center"
											mt={[6]}
											tabIndex={0}
											css={`
												height: 50px;
												-webkit-transform-style: preserve-3d;
												-webkit-backface-visibility: hidden;
												border-radius: 6px;
												cursor: pointer;
												transition: 0.5s;
												transform: translateZ(0);
												transform: scale(1);
												&:hover {
													transform: scale(1.05);
												}
											`}
										>
											<Text
												css={`
													transform: translateZ(0);
													-webkit-transform-style: preserve-3d;
													-webkit-backface-visibility: hidden;
												`}
												as="span"
												fontWeight={600}
											>
												DEDICATE NOW
											</Text>
										</Flex>
									</NextBase>
								</Flex>
							</Flex>
						</Flex>
					</LayoutPadding>
				</Flex>
				<CardSlider
					title={"Campaigns"}
					buttonTitle={"See all"}
					bg={colors.purple}
					href={"/campaigns/see-all"}
					data={recentCampaigns}
				/>
			</Layout>
		</React.Fragment>
	);
};

export async function getServerSideProps({ query }) {
	const { data, error } = await client.query({
		query: GET_CAMPAIGN_INDEX,
		variables: gqlVariables,
	});

	return {
		props: {
			data: error ? {} : data,
			error: error ? true : false,
		},
	};
}

export default Index;
